/* global BigInt */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config';
import '../styles/procurement-admin.css';

const PAGE_SIZE=50;
const STATUS_OPTIONS=['PREPARED','DEPARTED','RECEIVED','CANCELLED'];

const KG_SCALE=BigInt(1000);

function milliKg(value) {
  const match=/^([+-]?)(\d+)(?:\.(\d{1,3}))?$/.exec(String(value??'').trim());
  if (!match) return null;
  const whole=BigInt(match[2])*KG_SCALE;
  const fraction=BigInt((match[3]||'').padEnd(3,'0')||'0');
  const amount=whole+fraction;
  return match[1]==='-'?-amount:amount;
}

function formatMilliKg(amount, signed=false) {
  const negative=amount<BigInt(0);
  const absolute=negative?-amount:amount;
  const prefix=negative?'-':signed&&absolute>BigInt(0)?'+':'';
  return `${prefix}${absolute/KG_SCALE}.${String(absolute%KG_SCALE).padStart(3,'0')}`;
}

function kgLabel(value, signed=false) {
  const amount=milliKg(value);
  return amount===null
    ?value===null||value===undefined?'—':String(value)
    :formatMilliKg(amount,signed);
}

function receiptTotals(dispatch, receipt) {
  const expected=milliKg(dispatch.total_expected_net_kg);
  const receivedItems=Array.isArray(receipt?.items)?receipt.items:[];
  const headerReceived=milliKg(receipt?.received_total_net_kg);
  const received=headerReceived===null?receivedItems.reduce((total,item)=>{
    const value=milliKg(item.received_net_kg);
    return value===null?total:total+value;
  },BigInt(0)):headerReceived;
  return {expected,received,variance:expected===null?null:received-expected};
}

function dateLabel(value) {
  if (!value) return '—';
  const date=new Date(value);
  return Number.isNaN(date.getTime())?String(value):date.toLocaleString();
}

function optionName(row) {
  return row?.name||row?.code||String(row?.id??'');
}

function timestampFilter(value,endOfDay=false) {
  if (!value) return '';
  return `${value}T${endOfDay?'23:59:59.999':'00:00:00.000'}Z`;
}

export default function ProcurementDispatchMonitor() {
  const [filters,setFilters]=useState({status:'',warehouse_id:'',crop_id:'',aggregator_id:'',from:'',to:''});
  const [options,setOptions]=useState({warehouses:[],crops:[],aggregators:[]});
  const [rows,setRows]=useState([]);
  const [total,setTotal]=useState(0);
  const [offset,setOffset]=useState(0);
  const [selected,setSelected]=useState(null);
  const [loading,setLoading]=useState(true);
  const [message,setMessage]=useState('');

  useEffect(()=>{
    let current=true;
    fetch(`${API_BASE}/procurement/v1/bootstrap`,{headers:{Authorization:localStorage.getItem('token')}})
      .then(async response=>{
        const result=await response.json();
        if(!response.ok) throw new Error(result.message||'Procurement request failed');
        if(current) setOptions({warehouses:result.warehouses||[],crops:result.crops||[],aggregators:result.aggregators||[]});
      }).catch(()=>{});
    return ()=>{current=false;};
  },[]);

  const query=useMemo(()=>{
    const params=new URLSearchParams({limit:String(PAGE_SIZE),offset:String(offset)});
    for(const key of ['status','warehouse_id','crop_id','aggregator_id']) if(filters[key]) params.set(key,filters[key]);
    if(filters.from) params.set('from',timestampFilter(filters.from));
    if(filters.to) params.set('to',timestampFilter(filters.to,true));
    return params.toString();
  },[filters,offset]);

  const load=useCallback(async()=>{
    setLoading(true);setMessage('');
    try {
      const response=await fetch(`${API_BASE}/procurement/v1/admin/dispatches?${query}`,{
        headers:{Authorization:localStorage.getItem('token')},
      });
      const result=await response.json();
      if(!response.ok) throw new Error(result.message||'Procurement request failed');
      setRows(Array.isArray(result.rows)?result.rows:[]);
      setTotal(Number(result.total)||0);
      setSelected(previous=>result.rows?.find(row=>row.id===previous?.id)||null);
    } catch(error) {
      setRows([]);setTotal(0);setSelected(null);setMessage(error.message);
    } finally {setLoading(false);}
  },[query]);

  useEffect(()=>{load();},[load]);

  function updateFilter(key,value) {
    setFilters(previous=>({...previous,[key]:value}));
    setOffset(0);
    setSelected(null);
  }

  const filtered=Object.values(filters).some(Boolean);
  const pageNumber=total===0?0:Math.floor(offset/PAGE_SIZE)+1;
  const pageCount=Math.ceil(total/PAGE_SIZE);
  const selectedReceiptTotals=selected?.receipt?receiptTotals(selected,selected.receipt):null;

  return <section className="proc-admin proc-admin--dispatch-monitor" aria-label="Procurement Dispatch Monitor">
    <h2>Procurement Dispatch Monitor</h2>
    <p className="proc-admin-dispatch-guidance">Dispatch Monitor is read-only. Quantities shown in the list are the expected dispatched quantities. Warehouse Receipt records physical arrival and received quantities.</p>
    {message&&<p role="alert">{message}</p>}
    <div className="proc-admin-dispatch-filters" role="group" aria-label="Dispatch filters">
      <label>Status<select aria-label="Status" value={filters.status} onChange={event=>updateFilter('status',event.target.value)}>
        <option value="">All statuses</option>{STATUS_OPTIONS.map(value=><option key={value} value={value}>{value}</option>)}
      </select></label>
      <label>Warehouse<select aria-label="Warehouse" value={filters.warehouse_id} onChange={event=>updateFilter('warehouse_id',event.target.value)}>
        <option value="">All warehouses</option>{options.warehouses.map(row=><option key={row.id} value={row.id}>{row.name}{row.active===false?' (Inactive)':''}</option>)}
      </select></label>
      <label>Crop<select aria-label="Crop" value={filters.crop_id} onChange={event=>updateFilter('crop_id',event.target.value)}>
        <option value="">All crops</option>{options.crops.map(row=><option key={row.id} value={row.id}>{optionName(row)}</option>)}
      </select></label>
      <label>Aggregator<select aria-label="Aggregator" value={filters.aggregator_id} onChange={event=>updateFilter('aggregator_id',event.target.value)}>
        <option value="">All Aggregators</option>{options.aggregators.map(row=><option key={row.id} value={row.id}>{row.name}</option>)}
      </select></label>
      <label>From date<input aria-label="From date" type="date" value={filters.from} onChange={event=>updateFilter('from',event.target.value)} /></label>
      <label>To date<input aria-label="To date" type="date" value={filters.to} onChange={event=>updateFilter('to',event.target.value)} /></label>
    </div>
    {loading?<p role="status">Loading dispatches…</p>:rows.length===0
      ?<p>{filtered?'No dispatches match the selected filters.':'No Procurement dispatches have been recorded yet.'}</p>
      :<div className="proc-admin-dispatch-table-scroll" role="region" aria-label="Dispatch list" tabIndex={0}><table className="proc-admin-dispatch-table"><thead><tr>
        <th>Dispatch</th><th>Aggregator</th><th>Crop / Variety</th><th>Field Grade</th><th>Warehouse</th>
        <th>Expected kg</th><th>Procurements</th><th>Status</th><th>Prepared</th><th>Departure</th>
      </tr></thead><tbody>{rows.map(row=><tr key={row.id} onClick={()=>setSelected(row)}>
        <td><button className="proc-admin-dispatch-reference" type="button" aria-label={`Open dispatch ${row.id}`} onClick={event=>{event.stopPropagation();setSelected(row);}}>{row.id.slice(0,8)}</button></td>
        <td>{row.aggregator_name}</td><td>{row.crop_name} / {row.variety_name}</td><td>{row.field_grade_name}</td>
        <td>{row.warehouse_name}</td><td className="proc-admin-dispatch-number">{kgLabel(row.total_expected_net_kg)}</td><td>{row.procurement_count}</td>
        <td><span className={`proc-admin-dispatch-status proc-admin-dispatch-status--${String(row.state).toLowerCase()}`}>{row.state}</span></td>
        <td>{dateLabel(row.prepared_at)}</td><td>{dateLabel(row.departure_occurred_at)}</td>
      </tr>)}</tbody></table></div>}
    {!loading&&<nav className="proc-admin-dispatch-pagination" aria-label="Dispatch pagination">
      <button type="button" disabled={offset===0} onClick={()=>setOffset(value=>Math.max(0,value-PAGE_SIZE))}>Previous</button>
      <span>{total===0?'0 results':`Page ${pageNumber} of ${pageCount} · ${total} result${total===1?'':'s'}`}</span>
      <button type="button" disabled={offset+PAGE_SIZE>=total} onClick={()=>setOffset(value=>value+PAGE_SIZE)}>Next</button>
    </nav>}
    {selected&&<aside className="proc-admin-dispatch-detail" aria-label="Dispatch detail">
      <div className="proc-admin-dispatch-detail-heading"><h3>Dispatch details</h3><button type="button" onClick={()=>setSelected(null)}>Close details</button></div>
      <div className="proc-admin-dispatch-detail-grid">
        <section className="proc-admin-dispatch-detail-group" aria-label="Dispatch"><h4>Dispatch</h4><dl>
          <dt>Dispatch ID</dt><dd>{selected.id}</dd><dt>Status</dt><dd><span className={`proc-admin-dispatch-status proc-admin-dispatch-status--${String(selected.state).toLowerCase()}`}>{selected.state}</span></dd>
          <dt>Aggregator</dt><dd>{selected.aggregator_name}</dd><dt>Destination warehouse</dt><dd>{selected.warehouse_name}</dd>
        </dl></section>
        <section className="proc-admin-dispatch-detail-group" aria-label="Product"><h4>Product</h4><dl>
          <dt>Crop</dt><dd>{selected.crop_name}</dd><dt>Variety</dt><dd>{selected.variety_name}</dd><dt>Field Grade</dt><dd>{selected.field_grade_name}</dd>
        </dl></section>
        <section className="proc-admin-dispatch-detail-group" aria-label="Dispatch quantities"><h4>Dispatch quantities</h4><dl>
          <dt>Expected total (kg)</dt><dd className="proc-admin-dispatch-number">{kgLabel(selected.total_expected_net_kg)}</dd>
          <dt>Procurement count</dt><dd>{selected.procurement_count}</dd>
          {selected.items?.length>0&&<><dt>Procurement items</dt><dd><ul>{selected.items.map(item=><li key={item.procurement_id}>{item.procurement_id} — {kgLabel(item.expected_net_kg)} kg</li>)}</ul></dd></>}
        </dl></section>
        <section className="proc-admin-dispatch-detail-group" aria-label="Timeline"><h4>Timeline</h4><dl>
          <dt>Prepared</dt><dd>{dateLabel(selected.prepared_at)}</dd>
          {selected.departure_occurred_at&&<><dt>Physical departure</dt><dd>{dateLabel(selected.departure_occurred_at)}</dd></>}
          {selected.departure_recorded_at&&<><dt>Departure recorded</dt><dd>{dateLabel(selected.departure_recorded_at)}</dd></>}
          {selected.cancelled_at&&<><dt>Cancelled</dt><dd>{dateLabel(selected.cancelled_at)}</dd></>}
          {selected.cancellation_reason&&<><dt>Cancellation reason</dt><dd>{selected.cancellation_reason}</dd></>}
        </dl></section>
        {(selected.vehicle_number||selected.transporter_name||selected.contact||selected.challan_reference||(selected.bag_count!==null&&selected.bag_count!==undefined)||selected.remarks)&&<section className="proc-admin-dispatch-detail-group" aria-label="Transport"><h4>Transport</h4><dl>
          {selected.vehicle_number&&<><dt>Vehicle</dt><dd>{selected.vehicle_number}</dd></>}
          {selected.transporter_name&&<><dt>Transporter / driver</dt><dd>{selected.transporter_name}</dd></>}
          {selected.contact&&<><dt>Contact</dt><dd>{selected.contact}</dd></>}
          {selected.challan_reference&&<><dt>Challan / reference</dt><dd>{selected.challan_reference}</dd></>}
          {selected.bag_count!==null&&selected.bag_count!==undefined&&<><dt>Bag count</dt><dd>{selected.bag_count}</dd></>}
          {selected.remarks&&<><dt>Remarks</dt><dd>{selected.remarks}</dd></>}
        </dl></section>}
      </div>
      {selected.receipt&&<section className="proc-admin-dispatch-receipt" aria-label="Warehouse receipt"><h4>Warehouse Receipt</h4>
        <dl>
          <dt>Physical receipt</dt><dd>{dateLabel(selected.receipt.occurred_at)}</dd>
          <dt>Receipt recorded</dt><dd>{dateLabel(selected.receipt.recorded_at)}</dd>
          <dt>Expected total (kg)</dt><dd className="proc-admin-dispatch-number">{kgLabel(selected.total_expected_net_kg)}</dd>
          <dt>Received total (kg)</dt><dd className="proc-admin-dispatch-number">{selectedReceiptTotals.received===null?'—':formatMilliKg(selectedReceiptTotals.received)}</dd>
          <dt>Received kg − expected kg (variance)</dt><dd className="proc-admin-dispatch-number">{selectedReceiptTotals.variance===null?'—':formatMilliKg(selectedReceiptTotals.variance,true)}</dd>
          {selected.receipt.dispatch_variance_reason&&<><dt>Dispatch variance reason</dt><dd>{selected.receipt.dispatch_variance_reason}</dd></>}
          <dt>Procurement reconciliation</dt><dd>{selected.receipt.procurement_reconciled===false?'Not individually reconciled':'Reconciled Procurement-wise'}</dd>
        </dl>
        <ul className="proc-admin-dispatch-receipt-items">{(Array.isArray(selected.items)?selected.items:[]).map(item=>{
          const receiptItem=(Array.isArray(selected.receipt.items)?selected.receipt.items:[])
            .find(line=>line.procurement_id===item.procurement_id);
          if (!receiptItem) return <li key={item.procurement_id}>{item.procurement_id} — receipt item unavailable</li>;
          const expected=milliKg(item.expected_net_kg);
          const itemReceived=milliKg(receiptItem.received_net_kg);
          const variance=expected===null||itemReceived===null?null:itemReceived-expected;
          if(selected.receipt.procurement_reconciled===false) return <li key={item.procurement_id}>
            {item.procurement_id} — Dispatched / custody reference: {kgLabel(item.expected_net_kg)} kg; Warehouse item weight: Not individually reconciled
          </li>;
          return <li key={item.procurement_id}>
            {item.procurement_id} — Dispatched {kgLabel(item.expected_net_kg)} kg; reconciled received {kgLabel(receiptItem.received_net_kg)} kg; variance {variance===null?'—':formatMilliKg(variance,true)} kg
            {receiptItem.variance_reason&&<>; Variance reason: {receiptItem.variance_reason}</>}
          </li>;
        })}</ul>
      </section>}
    </aside>}
  </section>;
}
